import { Check, X } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  OnboardingProgress,
  OnboardingStep,
} from "../hooks/useOnboardingProgress";

interface SetupPillProps {
  progress: OnboardingProgress;
  pulseTrigger: number;
  onStepClick?: () => void;
}

interface StepConfig {
  key: OnboardingStep;
  label: string;
  path: string;
}

const STEPS: StepConfig[] = [
  { key: "brandKit", label: "Add brand kit", path: "/brand-kits" },
  { key: "connect", label: "Connect account", path: "/settings" },
  { key: "generate", label: "Make first post", path: "/generate" },
  { key: "autopilot", label: "Get daily suggested posts", path: "/ideas?addSource=1" },
];

export function SetupPill({ progress, pulseTrigger, onStepClick }: SetupPillProps) {
  const navigate = useNavigate();
  const [isPulsing, setIsPulsing] = useState(false);
  const completedFiredRef = useRef(false);

  useEffect(() => {
    if (pulseTrigger === 0) return;
    setIsPulsing(true);
    const timer = setTimeout(() => setIsPulsing(false), 600);
    return () => clearTimeout(timer);
  }, [pulseTrigger]);

  useEffect(() => {
    if (progress.allDone && !completedFiredRef.current) {
      completedFiredRef.current = true;
      const daysSinceSignup = 0;
      if (process.env.REACT_APP_POSTHOG_DISABLED !== "true") posthog.capture("onboarding_pill_completed", {
        days_since_signup: daysSinceSignup,
      });
    }
  }, [progress.allDone]);

  if (
    progress.loading ||
    progress.allDone ||
    progress.dismissed
  ) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    progress.dismiss();
    if (process.env.REACT_APP_POSTHOG_DISABLED !== "true") posthog.capture("onboarding_pill_dismissed", {
      done_count: progress.doneCount,
    });
  };

  const handleStepClick = (step: StepConfig, surface: "sidebar" | "mobile_drawer") => {
    posthog.capture("onboarding_pill_step_clicked", {
      step: step.key,
      surface,
      incomplete_count: 4 - progress.doneCount,
    });
    navigate(step.path);
    onStepClick?.();
  };

  return (
    <section
      aria-labelledby="setup-heading"
      className={cn(
        "rounded-xl border border-[#EFE5DA] px-3 py-3 transition-colors duration-300 motion-reduce:transition-none",
        isPulsing ? "bg-[#FFE5D9]" : "bg-[#FBF6F1]"
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 id="setup-heading" className="text-sm font-medium text-foreground">
          Setup
        </h2>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground tabular-nums">
            {`${progress.doneCount} of ${STEPS.length}`}
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Hide setup checklist"
            className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-black/[0.04] transition-colors"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="h-1 rounded-full bg-[#EFE5DA] mb-2.5 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#FF5C35] transition-all duration-500 ease-out"
          style={{ width: `${(progress.doneCount / STEPS.length) * 100}%` }}
        />
      </div>

      <ul className="flex flex-col gap-0.5">
        {STEPS.map((step) => {
          const done = progress.steps[step.key];
          const isNext = !done && progress.nextStep === step.key;
          return (
            <li key={step.key}>
              <button
                type="button"
                onClick={() => !done && handleStepClick(step, "sidebar")}
                disabled={done}
                className={cn(
                  "w-full flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors min-w-0",
                  done
                    ? "cursor-default"
                    : "hover:bg-black/[0.03] cursor-pointer"
                )}
              >
                {done ? (
                  <div className="h-3.5 w-3.5 rounded-full bg-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <Check className="h-2 w-2 text-white" strokeWidth={3} aria-hidden="true" />
                  </div>
                ) : (
                  <div
                    className={cn(
                      "h-3.5 w-3.5 rounded-full border-[1.5px] flex-shrink-0",
                      isNext ? "border-gray-500" : "border-gray-300"
                    )}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={cn(
                    "text-sm leading-tight truncate",
                    done
                      ? "text-gray-400 line-through decoration-gray-300"
                      : isNext
                        ? "font-medium text-gray-800"
                        : "text-gray-600"
                  )}
                >
                  {step.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
